/**
 * @description: 接口请求的权限数据处理
 * 父级的权限勾选需要和子级的 ‘查看’ 权限关联。
 * 如果查看权限勾选，其他权限没有勾选，此时角色管理给后端的父级状态是未勾选的
 * 但该种情况在需求里是需要父级的是被勾选的。
 * 根据chose字段判断是否有该权限
 *
 * 12.14 此功能后端改过了，由后端判断是否有父级权限
 * @param {*} menus
 * @return {*} format menus
 */

import { createStore } from "vuex"
import $fetch from "@/service/fetch"
import { getCurrentUserInfo, getCurrentAuthor } from "@/api/login"
import { resetPassword } from "@/views/login/components/dialogs/index"
import { flattenTreeData } from "@/service"
import { routes } from "@/router"
import createPersistedState from 'vuex-persistedstate'

const calcFirstMenuPath = (routesData, menus) => {
  for (const rItem of routesData) {
    if (!rItem) continue
    if (rItem.path === '/' || rItem.path === '/dashboard' || rItem.path === '/login' || rItem.path === '/404') continue
    if (!rItem.meta || rItem.meta.hidden) continue
    if (!rItem.code) continue
    if (rItem.children && rItem.children.length) {
      const visibleChildren = rItem.children.filter(c => menus.find(m => m.path === c.path))
      if (visibleChildren.length > 0) {
        const firstChild = visibleChildren[0]
        return { path: firstChild.path, name: firstChild.meta?.title || firstChild.name }
      }
    }
  }
  return null
}

const store = createStore({
  plugins: [createPersistedState({ paths: ['user', 'menu', 'visitedMenu', 'firstMenuPath', 'orderColumns', 'shipColumns'] })],
  state: {
    user: null,
    menu: [], // 顶部菜单栏
    firstMenuPath: null, // 登录后有权限的第一条菜单
    visitedMenu: [],
    viewCacheVersionMap: {},
    orderColumns: [],
    shipColumns: [],
  },
  mutations: {
    SET_ORDERCOLUMNDS: (state, info) => {
      state.orderColumns = info
    },
    SET_SHIPCOLUMNDS: (state, info) => {
      state.shipColumns = info
    },
    REMOVE_ORDERCOLUMNDS: (state) => {
      state.orderColumns = []
    },
    REMOVE_SHIPCOLUMNDS: (state) => {
      state.shipColumns = []
    },
    SET_USERINFO: (state, info) => {
      state.user = info
    },
    SET_MENU: (state, data) => {
      state.menu = data
    },
    SET_FIRST_MENU_PATH: (state, data) => {
      state.firstMenuPath = data
    },
    REMOVE_USERS: state => {
      // state.menu = []
      state.user = null
      // state.permissions = []
    },
    SET_VISITEDMENU: (state, data) => {
      if (!data) return
      let index = state.visitedMenu.findIndex(v =>
        {
          if (v.label && v.query) {
            return v.path == data.path && v.query[v.label] == data.query[data.label]
          } else {
            return v.path == data.path
          }
        }
      )
      if (index == -1) {
        if (state.visitedMenu.length >= 20) {
          state.visitedMenu.splice(1, 1)
        }
        state.visitedMenu.push(data)
      } else {
        state.visitedMenu[index].name = data.name
      }
    },
    DEL_VISITEDMENU: (state, data) => {
      if (!data) return
      let index = state.visitedMenu.findIndex(v => {
        if (v.label && v.query && data.label && data.query) {
          return v.path == data.path && v.query[v.label] == data.query[data.label]
        }
        return v.path == data.path
      })
      if (index > -1) {
        state.visitedMenu.splice(index, 1)
      }
      console.log(state.visitedMenu, data, 'data')
    },
    BUMP_VIEW_CACHE_VERSION: (state, fullPath) => {
      if (!fullPath) return
      const key = String(fullPath)
      const current = Number(state.viewCacheVersionMap[key] || 0)
      state.viewCacheVersionMap[key] = current + 1
    },
    REMOVE_VIEWS: (state) => {
      const first = state.firstMenuPath
      state.visitedMenu = first
        ? [{ name: first.name, path: first.path }]
        : [{ name: '首页', path: '/dashboard' }]
      state.viewCacheVersionMap = {}
    }
  },
  actions: {
    // 获取当前登录的用户信息
    GetUserInfo({ commit }) {
      // 当前用户身份信息 权限点位等等
      return new Promise((resolve, reject) => {
        getCurrentUserInfo()
          .then(res => {
            commit("SET_USERINFO", res)
            const { needChangePsw } = res
            store.dispatch("GetUserAuthor").then(() => {
              resolve(res)
            })
            // if (needChangePsw) {
            //   changePassword()
            // }
          })
          .catch(err => {
            console.log(err)
            reject(err)
          })
      })
    },

    // 获取当前登录的用户权限
    GetUserAuthor({ commit, state }) {
      return new Promise((resolve, reject) => {
        getCurrentAuthor()
          .then(res => {
            const allPerms = flattenTreeData(res)
            console.log(allPerms, 'allPerms')
            // 页面内权限点
            const perms = allPerms
              .filter(i => {
                return i.chose === 1 && i.perms
              })
              .map(i => i.perms)
            // commit("SET_PAGEPERMS", perms)

            // TopMenu按钮菜单
            const menus = res
            // const menus = res.filter(i => i.chose === 1)
            commit("SET_MENU", allPerms)

            // 取第一条有权限的菜单
            const first = calcFirstMenuPath(routes, allPerms)
            commit("SET_FIRST_MENU_PATH", first)
            // 有首页权限则默认落首页，否则落第一个有权限的菜单
            const canSeeHome = allPerms.some(m => m && m.path === '/dashboard')
            const fallbackTab = canSeeHome
              ? { name: '首页', path: '/dashboard' }
              : (first ? { name: first.name, path: first.path } : { name: '首页', path: '/dashboard' })
            // 初始化/重置 visitedMenu
            if (!state.visitedMenu || state.visitedMenu.length === 0) {
              commit("SET_VISITEDMENU", fallbackTab)
            }

            resolve(menus)
          })
          .catch(err => {
            console.log(err)
            reject(err)
          })
      })
    },

    logout({ commit }) {
      // resetRouter();
      // 退出登陆后所有弹窗消失
      let doms = document.getElementsByClassName("el-overlay")
      let domArr = Array.from(doms)
      domArr.forEach(current => {
        current.style.display = "none"
      })
      commit("REMOVE_USERS")
      commit("REMOVE_VIEWS")
      commit('REMOVE_ORDERCOLUMNDS')
      commit('REMOVE_SHIPCOLUMNDS')
    },
    // 访问过的菜单
    getVisitedMenu({ commit }, res) {
      // 当前用户身份信息 权限点位等等
      return new Promise(resolve => {
        commit("SET_VISITEDMENU", res)
        // store.dispatch("")
        resolve(res)
      })
    },

  },
  modules: {
    // modules
  },
  getters: {
    pagePerms: function (state) {
      return state.pagePerms
    },
  },
  // plugins: [vuexLocal.plugin],
})

const changePassword = () => {
  resetPassword({
    config: {
      force: true,
    },
  }).then(
    () => {},
    () => {}
  )
}

export default store
